import { CometChat } from "@cometchat/chat-sdk-javascript";

/** An in-progress "@…" token sitting immediately before the caret. */
export type MentionQuery = { query: string; start: number };

/* Up to two words after the "@" — enough to narrow "@Jane D" to "Jane Doe"
   without the menu staying open across a whole sentence the moment someone
   types an email address or a stray "@". */
const IN_PROGRESS = /(?:^|\s)@([A-Za-z][A-Za-z'-]*(?: [A-Za-z][A-Za-z'-]*)?)?$/;

/**
 * Detects whether the caret is currently inside an "@mention" being typed.
 * Anchored to the start of the string or a space so "user@example.com"
 * never opens the picker.
 */
export function findMentionQuery(text: string, cursor: number): MentionQuery | null {
  const before = text.slice(0, cursor);
  const match = IN_PROGRESS.exec(before);
  if (!match) return null;
  const query = match[1] || "";
  return { query, start: cursor - query.length - 1 };
}

/** Replaces the in-progress token with the picked name, plus a trailing space. */
export function applyMention(
  text: string,
  active: MentionQuery,
  cursor: number,
  name: string
): { text: string; cursor: number } {
  const inserted = `@${name} `;
  const next = text.slice(0, active.start) + inserted + text.slice(cursor);
  return { text: next, cursor: active.start + inserted.length };
}

/** Ranks candidates for the query: prefix matches before substring matches. */
export function filterMentionCandidates(
  candidates: CometChat.User[],
  query: string,
  limit = 6
): CometChat.User[] {
  const q = query.trim().toLowerCase();
  if (!q) return candidates.slice(0, limit);
  const scored: Array<{ user: CometChat.User; score: number }> = [];
  for (const u of candidates) {
    const name = (u.getName() || "").toLowerCase();
    if (!name) continue;
    // A surname match still counts, so "@doe" finds "Jane Doe".
    const wordStart = name.split(/\s+/).some((w) => w.startsWith(q));
    if (name.startsWith(q)) scored.push({ user: u, score: 0 });
    else if (wordStart) scored.push({ user: u, score: 1 });
    else if (name.includes(q)) scored.push({ user: u, score: 2 });
  }
  return scored.sort((a, b) => a.score - b.score).slice(0, limit).map((s) => s.user);
}

/**
 * Resolves which users a finished draft actually mentions, by matching the
 * literal "@Name" text back against the candidate list. Passing these to
 * TextMessage.setMentionedUsers is what makes a mention a REAL CometChat
 * mention (server-side, survives a reload, drives hasMentionedMe) rather
 * than text that merely looks highlighted to whoever renders it.
 */
export function resolveMentionedUsers(
  text: string,
  candidates: CometChat.User[]
): CometChat.User[] {
  const out: CometChat.User[] = [];
  for (const u of candidates) {
    const name = u.getName();
    if (!name) continue;
    if (text.includes(`@${name}`) && !out.some((x) => x.getUid() === u.getUid())) out.push(u);
  }
  return out;
}
